import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { MapsProvider } from "./contexts/MapsContext";
import PWAInstallPrompt from "./components/common/PWAInstallPrompt";
import Sidekick from "./components/common/Sidekick";
import MaintenanceGuard from "./components/common/MaintenanceGuard";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { initErrorReporter } from "./utils/errorReporter";

initErrorReporter();

// Customer Pages
import CustomerHome from "./pages/customer/Home";
import ServiceDetail from "./pages/customer/ServiceDetail";
import BookingFlow from "./pages/customer/BookingFlow";
import BookingTracking from "./pages/customer/BookingTracking";
import CustomerBookings from "./pages/customer/Bookings";
import CustomerProfile from "./pages/customer/Profile";
import CustomerWallet from "./pages/customer/Wallet";
import CustomerMessages from "./pages/customer/Messages";
import ServiceSearch from "./pages/customer/ServiceSearch";
import CustomerReferral from "./pages/customer/Referral";

// Provider Pages
import ProviderHome from "./pages/provider/Home";
import ProviderJobs from "./pages/provider/Jobs";
import ProviderEarnings from "./pages/provider/Earnings";
import ProviderProfile from "./pages/provider/Profile";
import ProviderAvailability from "./pages/provider/Availability";
import ProviderStaff from "./pages/provider/Staff";
import ProviderSupport from "./pages/provider/Support";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProviders from "./pages/admin/Providers";
import AdminBookings from "./pages/admin/Bookings";
import AdminSettings from "./pages/admin/Settings";
import AdminCMS from "./pages/admin/CMS";
import AdminPromoCodes from "./pages/admin/PromoCodes";
import AdminDisputes from "./pages/admin/Disputes";
import AdminUserReports from "./pages/admin/UserReports";
import AdminUsers from "./pages/admin/Users";
import AdminReports from "./pages/admin/Reports";
import AdminBookingDetail from "./pages/admin/BookingDetail";
import AdminCategories from "./pages/admin/Categories";
import AdminWithdrawals from "./pages/admin/Withdrawals";
import AdminAuditLogs from "./pages/admin/AuditLogs";
import AdminCategoryAnalytics from "./pages/admin/CategoryAnalytics";
import AdminDataManagement from "./pages/admin/DataManagement";
import AdminTransactions from "./pages/admin/Transactions";
import AdminLocations from "./pages/admin/Locations";
import AdminInsuranceClaims from "./pages/admin/InsuranceClaims";
import AdminInsuranceDeclaration from "./pages/admin/InsuranceDeclaration";
import AdminBanners from "./pages/admin/Banners";
import AdminDataRetention from "./pages/admin/DataRetention";
import AdminAccounting from "./pages/admin/Accounting";
import AdminServices from "./pages/admin/Services";
import AdminErrorLogs from "./pages/admin/ErrorLogs";
import AdminMessages from "./pages/admin/Messages";
import AdminOutreach from "./pages/admin/Outreach";
import AdminKnowledgeBase from "./pages/admin/KnowledgeBase";

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";

// Landing Page
import LandingPage from "./pages/Landing";

// Profile & Verification Pages
import ProfileSettings from "./pages/ProfileSettings";
import FaceVerification from "./pages/FaceVerification";

// Shared Pages
import Disputes from "./pages/Disputes";

// Public Pages
import PublicTracking from "./pages/PublicTracking";
import LegalPage from "./pages/LegalPage";

// Common Pages
import PaymongoPayment from "./pages/common/PaymongoPayment";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Staff roles (manager, customer_service, accountant) can access admin routes 
  // Permission checks happen on the page level
  const effectiveRoles = [...(allowedRoles || [])];
  if (allowedRoles?.includes("admin")) {
    effectiveRoles.push("manager", "customer_service", "accountant");
  }
  
  if (effectiveRoles.length > 0 && !effectiveRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "manager") return <Navigate to="/admin" replace />;
    if (user.role === "customer_service") return <Navigate to="/admin" replace />;
    if (user.role === "accountant") return <Navigate to="/admin" replace />;
    if (user.role === "provider") return <Navigate to="/provider" replace />;
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Role-based redirect
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "manager") return <Navigate to="/admin" replace />;
  if (user.role === "customer_service") return <Navigate to="/admin" replace />;
  if (user.role === "accountant") return <Navigate to="/admin" replace />;
  if (user.role === "provider") return <Navigate to="/provider" replace />;
  return <Navigate to="/home" replace />;
};

// Landing page wrapper - redirect authenticated users to their dashboard
const LandingOrRedirect = () => {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) {
    if (user.role === "admin" || user.role === "manager") return <Navigate to="/admin" replace />;
    if (user.role === "provider") return <Navigate to="/provider" replace />;
    return <Navigate to="/home" replace />;
  }
  return <LandingPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingOrRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms" element={<LegalPage type="terms" />} />
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      
      {/* Role-based redirect */}
      <Route path="/dashboard" element={<RoleBasedRedirect />} />
      
      {/* Profile & Verification Routes (all authenticated users) */}
      <Route path="/profile/settings" element={
        <ProtectedRoute allowedRoles={["customer", "provider", "admin"]}>
          <ProfileSettings />
        </ProtectedRoute>
      } />
      <Route path="/verification" element={
        <ProtectedRoute allowedRoles={["customer", "provider", "admin"]}>
          <FaceVerification />
        </ProtectedRoute>
      } />
      
      {/* Customer Routes */}
      <Route path="/home" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <CustomerHome />
        </ProtectedRoute>
      } />
      <Route path="/service/:categoryId" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <ServiceDetail />
        </ProtectedRoute>
      } />
      <Route path="/book/:serviceId" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <BookingFlow />
        </ProtectedRoute>
      } />
      <Route path="/booking/:bookingId" element={
        <ProtectedRoute allowedRoles={["customer", "provider"]}>
          <BookingTracking />
        </ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <CustomerBookings />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <CustomerProfile />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <CustomerWallet />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <CustomerMessages />
        </ProtectedRoute>
      } />
      <Route path="/service-search" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <ServiceSearch />
        </ProtectedRoute>
      } />
      <Route path="/referral" element={
        <ProtectedRoute allowedRoles={["customer"]}>
          <CustomerReferral />
        </ProtectedRoute>
      } />
      
      {/* Payment Routes (Customer & Provider) */}
      <Route path="/payment" element={
        <ProtectedRoute allowedRoles={["customer", "provider"]}>
          <PaymongoPayment />
        </ProtectedRoute>
      } />
      
      {/* Provider Routes */}
      <Route path="/provider" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderHome />
        </ProtectedRoute>
      } />
      <Route path="/provider/jobs" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderJobs />
        </ProtectedRoute>
      } />
      <Route path="/provider/earnings" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderEarnings />
        </ProtectedRoute>
      } />
      <Route path="/provider/profile" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderProfile />
        </ProtectedRoute>
      } />
      <Route path="/provider/availability" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderAvailability />
        </ProtectedRoute>
      } />
      <Route path="/provider/support" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderSupport />
        </ProtectedRoute>
      } />
      <Route path="/provider/staff" element={
        <ProtectedRoute allowedRoles={["provider"]}>
          <ProviderStaff />
        </ProtectedRoute>
      } />
      
      {/* Shared Routes (Customer & Provider) */}
      <Route path="/disputes" element={
        <ProtectedRoute allowedRoles={["customer", "provider"]}>
          <Disputes />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/providers" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminProviders />
        </ProtectedRoute>
      } />
      <Route path="/admin/bookings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminBookings />
        </ProtectedRoute>
      } />
      <Route path="/admin/bookings/:bookingId" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminBookingDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/categories" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminCategories />
        </ProtectedRoute>
      } />
      <Route path="/admin/services" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminServices />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/data-management" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDataManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/data-retention" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDataRetention />
        </ProtectedRoute>
      } />
      <Route path="/admin/cms" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminCMS />
        </ProtectedRoute>
      } />
      <Route path="/admin/promo-codes" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminPromoCodes />
        </ProtectedRoute>
      } />
      <Route path="/admin/disputes" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDisputes />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-reports" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminUserReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/insurance-claims" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminInsuranceClaims />
        </ProtectedRoute>
      } />
      <Route path="/admin/insurance-declaration" element={
        <ProtectedRoute allowedRoles={["admin", "manager"]}>
          <AdminInsuranceDeclaration />
        </ProtectedRoute>
      } />
      <Route path="/admin/banners" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminBanners />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/withdrawals" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminWithdrawals />
        </ProtectedRoute>
      } />
      <Route path="/admin/transactions" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTransactions />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminAuditLogs />
        </ProtectedRoute>
      } />
      <Route path="/admin/error-logs" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminErrorLogs />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute allowedRoles={["admin", "manager", "customer_service", "accountant"]}>
          <AdminMessages />
        </ProtectedRoute>
      } />
      <Route path="/admin/knowledge-base" element={
        <ProtectedRoute allowedRoles={["admin", "manager", "customer_service"]}>
          <AdminKnowledgeBase />
        </ProtectedRoute>
      } />
      <Route path="/admin/outreach" element={
        <ProtectedRoute allowedRoles={["admin", "manager"]}>
          <AdminOutreach />
        </ProtectedRoute>
      } />
      <Route path="/admin/category-analytics" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminCategoryAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/admin/locations" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminLocations />
        </ProtectedRoute>
      } />
      <Route path="/admin/accounting" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminAccounting />
        </ProtectedRoute>
      } />
      
      {/* Public Tracking Page */}
      <Route path="/track/:shareToken" element={<PublicTracking />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Load site identity (title + favicon) on app start
  useEffect(() => {
    const BACKEND = window.location.origin;
    fetch(`${BACKEND}/api/cms/landing`).then(r => r.json()).then(data => {
      const si = data?.site_identity;
      if (si) {
        if (si.site_title) document.title = si.site_tagline ? `${si.site_title} - ${si.site_tagline}` : si.site_title;
        if (si.favicon) {
          const url = si.favicon.startsWith('http') ? si.favicon : `${BACKEND}${si.favicon}`;
          let link = document.querySelector("link[rel*='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = url;
        }
      }
    }).catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <MapsProvider>
            <ErrorBoundary>
              <MaintenanceGuard>
                <AppRoutes />
              </MaintenanceGuard>
            </ErrorBoundary>
            <Toaster richColors position="top-center" />
            <PWAInstallPrompt />
            <Sidekick />
          </MapsProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
